import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const EXPECTED_COLUMNS = [
  { key: 'work_order_number', label: 'Work Order Number', required: true, aliases: ['wo number', 'wo#', 'work order', 'workorder', 'work_order', 'wo_number', 'wonumber'] },
  { key: 'description', label: 'Description', required: false, aliases: ['desc', 'job description', 'work description'] },
  { key: 'location', label: 'Location', required: false, aliases: ['site', 'address', 'job site', 'job location'] },
  { key: 'assigned_to', label: 'Assigned To', required: false, aliases: ['technician', 'tech', 'employee', 'worker', 'assigned'] },
  { key: 'work_type', label: 'Work Type', required: false, aliases: ['type', 'category', 'job type'] },
  { key: 'priority', label: 'Priority', required: false, aliases: ['urgency', 'priority level'] },
  { key: 'amount', label: 'Amount', required: false, aliases: ['cost', 'price', 'total', 'invoice amount', 'value', '$'] },
  { key: 'notes', label: 'Notes', required: false, aliases: ['comments', 'remarks', 'note'] },
];

const normalizeHeader = (header) => header?.toString()?.trim()?.toLowerCase()?.replace(/\s+/g, ' ');

const mapHeaderToKey = (header) => {
  const normalized = normalizeHeader(header);
  for (const col of EXPECTED_COLUMNS) {
    if (normalized === col?.key || normalized === col?.label?.toLowerCase() || col?.aliases?.includes(normalized)) {
      return col?.key;
    }
  }
  return null;
};

const WorkOrderImportModal = ({ onClose }) => {
  const [step, setStep] = useState('upload'); // upload | preview | importing | done
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [previewData, setPreviewData] = useState([]);
  const [columnMap, setColumnMap] = useState({});
  const [importErrors, setImportErrors] = useState([]);
  const [importCount, setImportCount] = useState(0);
  const [importing, setImporting] = useState(false);

  const handleFileChange = (e) => {
    const selected = e?.target?.files?.[0];
    if (!selected) return;
    const ext = selected?.name?.split('.')?.pop()?.toLowerCase();
    if (!['xlsx', 'xls', 'csv']?.includes(ext)) {
      setFileError('Please upload an Excel (.xlsx, .xls) or CSV file.');
      return;
    }
    setFileError('');
    setFile(selected);
    parseFile(selected);
  };

  const parseFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX?.read(data, { type: 'array' });
        const sheetName = workbook?.SheetNames?.[0];
        const sheet = workbook?.Sheets?.[sheetName];
        const rows = XLSX?.utils?.sheet_to_json(sheet, { header: 1, defval: '' });

        if (rows?.length < 2) {
          setFileError('File must have at least a header row and one data row.');
          return;
        }

        const headers = rows?.[0];
        const map = {};
        headers?.forEach((h, i) => {
          const key = mapHeaderToKey(h);
          if (key) map[i] = key;
        });

        const dataRows = rows?.slice(1)?.filter(row => row?.some(cell => cell !== ''));
        const mapped = dataRows?.map(row => {
          const obj = {};
          Object.entries(map)?.forEach(([colIdx, key]) => {
            obj[key] = row?.[colIdx]?.toString()?.trim() || '';
          });
          return obj;
        });

        setColumnMap(map);
        setPreviewData(mapped);
        setStep('preview');
      } catch (err) {
        setFileError('Failed to parse file: ' + err?.message);
      }
    };
    reader?.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    const errors = [];
    const validRows = previewData?.filter((row, idx) => {
      if (!row?.work_order_number) {
        errors?.push(`Row ${idx + 2}: Missing Work Order Number`);
        return false;
      }
      return true;
    });

    if (validRows?.length === 0) {
      setImportErrors(['No valid rows to import. All rows are missing Work Order Number.']);
      return;
    }

    setImporting(true);
    setStep('importing');

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const batchId = `IMPORT-${Date.now()}`;

      const records = validRows?.map(row => ({
        work_order_number: row?.work_order_number,
        description: row?.description || null,
        location: row?.location || null,
        assigned_to: row?.assigned_to || null,
        work_type: row?.work_type || null,
        priority: row?.priority || 'Normal',
        amount: row?.amount ? parseFloat(row?.amount?.replace(/[^0-9.-]/g, '')) || null : null,
        notes: row?.notes || null,
        qa_status: 'Pending Review',
        import_batch: batchId,
        created_by: user?.id || null,
      }));

      const { error: insertError } = await supabase?.from('work_orders')?.insert(records);

      if (insertError) throw insertError;

      setImportCount(records?.length);
      setImportErrors(errors);
      setStep('done');
    } catch (err) {
      setImportErrors([err?.message]);
      setStep('preview');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const headers = EXPECTED_COLUMNS?.map(c => c?.label);
    const sample = [
      ['WO-2024-001', 'HVAC Maintenance', 'Building A - Floor 3', 'John Smith', 'Maintenance', 'Normal', '1500.00', 'Annual service'],
      ['WO-2024-002', 'Electrical Repair', 'Warehouse B', 'Jane Doe', 'Repair', 'High', '850.00', ''],
    ];
    const ws = XLSX?.utils?.aoa_to_sheet([headers, ...sample]);
    const wb = XLSX?.utils?.book_new();
    XLSX?.utils?.book_append_sheet(wb, ws, 'Work Orders');
    XLSX?.writeFile(wb, 'work_order_import_template.xlsx');
  };

  return (
    <div className="fixed inset-0 z-modal bg-black/50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Upload" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-foreground">Import Work Orders</h2>
              <p className="text-xs text-muted-foreground">Upload an Excel or CSV file to bulk import work orders</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Step: Upload */}
          {step === 'upload' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Upload an Excel (.xlsx, .xls) or CSV file. The first row should be column headers.
                </p>
                <button
                  onClick={downloadTemplate}
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <Icon name="Download" size={14} />
                  Download Template
                </button>
              </div>

              <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-all">
                <Icon name="FileSpreadsheet" size={40} className="text-muted-foreground mb-3" />
                <span className="text-sm font-medium text-foreground">Click to upload or drag & drop</span>
                <span className="text-xs text-muted-foreground mt-1">.xlsx, .xls, .csv supported</span>
                <input type="file" className="hidden" accept=".xlsx,.xls,.csv" onChange={handleFileChange} />
              </label>

              {fileError && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm flex items-center gap-2">
                  <Icon name="AlertCircle" size={16} />
                  {fileError}
                </div>
              )}

              <div className="bg-muted/40 rounded-lg p-4">
                <p className="text-xs font-semibold text-foreground mb-2">Supported Column Names:</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {EXPECTED_COLUMNS?.map(col => (
                    <div key={col?.key} className="text-xs text-muted-foreground">
                      <span className={col?.required ? 'text-destructive font-medium' : ''}>{col?.label}</span>
                      {col?.required && <span className="text-destructive ml-0.5">*</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step: Preview */}
          {step === 'preview' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name="CheckCircle" size={18} className="text-green-600" />
                  <span className="text-sm font-medium text-foreground">
                    {previewData?.length} rows ready to import
                  </span>
                </div>
                <button
                  onClick={() => { setStep('upload'); setFile(null); setPreviewData([]); }}
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  <Icon name="RefreshCw" size={14} />
                  Change File
                </button>
              </div>

              {importErrors?.length > 0 && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs font-semibold text-yellow-800 mb-1">Warnings:</p>
                  {importErrors?.map((e, i) => (
                    <p key={i} className="text-xs text-yellow-700">{e}</p>
                  ))}
                </div>
              )}

              <div className="overflow-x-auto border border-border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/40 border-b border-border">
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">#</th>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">WO Number *</th>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Description</th>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Location</th>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Assigned To</th>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Amount</th>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Priority</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData?.slice(0, 50)?.map((row, idx) => (
                      <tr key={idx} className={`border-b border-border ${!row?.work_order_number ? 'bg-red-50' : ''}`}>
                        <td className="px-3 py-2 text-muted-foreground">{idx + 1}</td>
                        <td className="px-3 py-2 font-mono font-medium text-foreground">
                          {row?.work_order_number || <span className="text-destructive">MISSING</span>}
                        </td>
                        <td className="px-3 py-2 text-foreground max-w-[150px] truncate">{row?.description || '—'}</td>
                        <td className="px-3 py-2 text-foreground">{row?.location || '—'}</td>
                        <td className="px-3 py-2 text-foreground">{row?.assigned_to || '—'}</td>
                        <td className="px-3 py-2 text-foreground">{row?.amount || '—'}</td>
                        <td className="px-3 py-2 text-foreground">{row?.priority || 'Normal'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewData?.length > 50 && (
                  <div className="px-3 py-2 text-xs text-muted-foreground bg-muted/20">
                    Showing first 50 of {previewData?.length} rows
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step: Importing */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-sm font-medium text-foreground">Importing work orders...</p>
              <p className="text-xs text-muted-foreground">Please wait, do not close this window</p>
            </div>
          )}

          {/* Step: Done */}
          {step === 'done' && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <Icon name="CheckCircle" size={32} className="text-green-600" />
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-foreground">Import Complete!</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Successfully imported <strong>{importCount}</strong> work orders
                </p>
              </div>
              {importErrors?.length > 0 && (
                <div className="w-full p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs font-semibold text-yellow-800 mb-1">{importErrors?.length} rows skipped:</p>
                  {importErrors?.map((e, i) => <p key={i} className="text-xs text-yellow-700">{e}</p>)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <Button variant="ghost" onClick={onClose}>
            {step === 'done' ? 'Close' : 'Cancel'}
          </Button>
          {step === 'preview' && (
            <Button
              variant="primary"
              iconName="Upload"
              onClick={handleImport}
              disabled={importing || previewData?.filter(r => r?.work_order_number)?.length === 0}
            >
              Import {previewData?.filter(r => r?.work_order_number)?.length} Work Orders
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkOrderImportModal;
